import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  CallStateBadge,
  LevelBadge,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
} from '@/components/ui';
import { formatDateTime, formatMoney, formatNumber } from '@/lib/format';

/**
 * One call, end to end, for an operator.
 *
 * `GET /admin/calls/{id}` has returned all of this since 1.0.81 and nothing
 * rendered it: an operator reading a support ticket could see a row on the
 * call history list and nothing underneath it, so the only way to look at a
 * finished call properly was to open the client's own portal as them.
 *
 * Deliberately the same shape the client's page uses, so the two are looking
 * at the same thing during a support conversation. It adds one section the
 * client never sees — what this single call actually cost, line by line.
 */

interface Detail {
  call: Record<string, any>;
  transcript: Array<{
    role: string;
    content: string;
    started_ms: number | null;
    ended_ms: number | null;
    confidence: number | null;
    created_at: string;
  }>;
  states: Array<{
    from_state: string | null;
    to_state: string;
    reason: string | null;
    source: string | null;
    created_at: string;
  }>;
  transfers: Array<{
    destination: string;
    kind: string;
    result: string;
    failure_reason: string | null;
    agent: string | null;
    started_at: string;
    ended_at: string | null;
  }>;
  usage: Array<{
    kind: string;
    model: string | null;
    quantity: number;
    unit: string;
    actual_cost: string | null;
    client_price: string | null;
  }>;
}

function seconds(value: number | null | undefined): string {
  if (!value) return '—';
  const whole = Math.round(value);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** Milliseconds from the start of the call, as m:ss. */
function stamp(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export default function AdminCallDetail() {
  const { callId } = useParams<{ callId: string }>();

  const [showLogs, setShowLogs] = useState(false);

  const detail = useQuery({
    queryKey: ['admin-call', callId],
    queryFn: () => api.get<Detail>(`/admin/calls/${callId}`),
    enabled: !!callId,
  });

  /**
   * The raw log for this one call.
   *
   * The call id has been in the context column of every line for releases and
   * there was no way to ask for it from the product — so "why did that call go
   * wrong" meant SSH and a grep, which is the thing keeping a log in a database
   * is supposed to avoid. Fetched only when asked for: most visits to this page
   * are not an investigation.
   */
  const logs = useQuery({
    queryKey: ['admin-call-logs', callId],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: number;
          created_at: string;
          level: string;
          source: string;
          message: string;
          context: Record<string, unknown>;
        }>;
        count: number;
      }>(`/admin/calls/${callId}/logs`),
    enabled: !!callId && showLogs,
  });

  if (detail.isPending) return <Loading />;
  if (detail.error)
    return <ErrorState error={detail.error} retry={() => detail.refetch()} />;

  const { call, transcript, states, transfers, usage } = detail.data;

  return (
    <div className="w-full">
      <PageHeader
        title={call.caller_number || 'Call'}
        description={`${call.client_name} · ${formatDateTime(call.started_at)}`}
        actions={
          <Link
            to={`/clients/${call.tenant_id}`}
            className="text-sm text-brand-600 hover:underline dark:text-brand-400"
          >
            Open client →
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="State"
          value={<CallStateBadge state={call.state} kind={call.bot_kind} />}
          dense
        />
        <StatCard
          label="Outcome"
          value={call.disposition ?? '—'}
          sub={call.end_reason ?? undefined}
          dense
        />
        <StatCard label="Length" value={seconds(call.duration_sec)} dense />
        <StatCard
          label="Lead score"
          value={call.lead_score ?? '—'}
          sub={call.qualified ? 'Qualified' : undefined}
          tone={call.qualified ? 'good' : 'default'}
          dense
        />
        <StatCard
          label="Cost"
          value={call.total_actual ? formatMoney(Number(call.total_actual)) : '—'}
          sub={
            call.total_client
              ? `billed ${formatMoney(Number(call.total_client))}`
              : undefined
          }
          dense
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="Transcript" padded={false}>
            {!transcript.length ? (
              <EmptyState
                title="Nothing was said"
                description="No speech was recognised on this call."
              />
            ) : (
              <ul className="divide-y divide-line">
                {transcript.map((turn, index) => (
                  <li key={index} className="flex gap-3 px-4 py-2.5">
                    <span className="w-10 shrink-0 pt-0.5 text-2xs tabular-nums text-ink-400">
                      {stamp(turn.started_ms)}
                    </span>
                    <span className="w-14 shrink-0">
                      <Badge tone={turn.role === 'bot' ? 'blue' : 'gray'}>
                        {turn.role}
                      </Badge>
                    </span>
                    <p className="min-w-0 flex-1 text-sm text-ink-800 dark:text-ink-100">
                      {turn.content}
                      {turn.confidence !== null &&
                        turn.confidence !== undefined &&
                        turn.confidence < 0.6 && (
                          <span
                            className="ml-2 text-2xs text-amber-600 dark:text-amber-400"
                            title="Speech recognition was unsure about this line"
                          >
                            low confidence
                          </span>
                        )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="What this call cost"
            description="Platform-side only. The client never sees this breakdown."
            padded={false}
          >
            {!usage.length ? (
              <EmptyState
                title="Nothing metered"
                description="No usage was recorded against this call."
              />
            ) : (
              <table className="glee-table">
                <thead>
                  <tr>
                    <th>What</th>
                    <th>Model</th>
                    <th className="text-right">Quantity</th>
                    <th className="text-right">Cost</th>
                    <th className="text-right">Billed</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.map((row, index) => (
                    <tr key={index}>
                      <td className="text-xs">
                        {row.kind.replace(/_/g, ' ').toLowerCase()}
                      </td>
                      <td className="font-mono text-2xs text-ink-500">
                        {row.model ?? '—'}
                      </td>
                      <td className="text-right tabular-nums text-xs">
                        {formatNumber(row.quantity)} {row.unit}
                      </td>
                      <td className="text-right tabular-nums text-xs">
                        {row.actual_cost ? formatMoney(Number(row.actual_cost)) : '—'}
                      </td>
                      <td className="text-right tabular-nums text-xs">
                        {row.client_price ? formatMoney(Number(row.client_price)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Where it came from">
            <dl className="space-y-2 text-sm">
              {[
                ['Client', call.client_name],
                ['Bot', call.bot_name],
                ['Dialer', call.dialer_name],
                ['Campaign', call.vicidial_campaign],
                ['Dialer user', call.vicidial_user],
                ['Lead id', call.vicidial_lead_id],
                ['Direction', call.direction],
                ['Mode', call.mode],
                ['Intent', call.intent],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex justify-between gap-3">
                  <dt className="text-ink-500">{label}</dt>
                  <dd className="truncate text-right text-ink-800 dark:text-ink-100">
                    {value ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card title="What happened, in order" padded={false}>
            {!states.length ? (
              <EmptyState title="No events" description="Nothing was recorded." />
            ) : (
              <ol className="divide-y divide-line text-xs">
                {states.map((event, index) => (
                  <li key={index} className="px-4 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <CallStateBadge state={event.to_state} kind={call.bot_kind} />
                      <span className="tabular-nums text-ink-400">
                        {formatDateTime(event.created_at)}
                      </span>
                    </div>
                    {event.reason && (
                      <p className="mt-1 text-ink-600 dark:text-ink-300">
                        {event.reason}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {!!transfers.length && (
            <Card title="Transfers" padded={false}>
              <ul className="divide-y divide-line text-xs">
                {transfers.map((transfer, index) => (
                  <li key={index} className="px-4 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-ink-800 dark:text-ink-100">
                        {transfer.destination}
                      </span>
                      <Badge
                        tone={transfer.result === 'CONNECTED' ? 'green' : 'amber'}
                      >
                        {transfer.result.toLowerCase()}
                      </Badge>
                    </div>
                    <p className="mt-1 text-ink-500">
                      {transfer.kind}
                      {transfer.agent ? ` · ${transfer.agent}` : ''}
                      {transfer.failure_reason ? ` · ${transfer.failure_reason}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card
            title="Raw log"
            description="Every line this call wrote."
            actions={
              <Link
                to={`/system-logs?call_id=${call.id}`}
                className="text-xs text-brand-600 hover:underline dark:text-brand-400"
              >
                Full viewer →
              </Link>
            }
            padded={false}
          >
            {!showLogs ? (
              <div className="p-4">
                <Button variant="secondary" onClick={() => setShowLogs(true)}>
                  Show raw log
                </Button>
              </div>
            ) : logs.isPending ? (
              <Loading />
            ) : !logs.data?.data.length ? (
              <EmptyState
                title="Nothing logged"
                description="No line on this platform carried this call's id."
              />
            ) : (
              <div className="max-h-[28rem] overflow-y-auto">
                <ul className="divide-y divide-line font-mono text-2xs">
                  {logs.data.data.map((row) => (
                    <li key={row.id} className="px-3 py-1.5">
                      <div className="flex items-start gap-2">
                        <span className="shrink-0 tabular-nums text-ink-400">
                          {row.created_at.slice(11, 23)}
                        </span>
                        <LevelBadge level={row.level} />
                        <span className="min-w-0 flex-1 font-sans text-ink-800 dark:text-ink-100">
                          {row.message}
                        </span>
                      </div>
                      {!!Object.keys(row.context ?? {}).length && (
                        <div className="mt-0.5 pl-[5.5rem] text-ink-500">
                          {Object.entries(row.context)
                            .filter(([key]) => key !== 'call_id')
                            .map(([key, value]) => `${key}=${String(value)}`)
                            .join('  ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
